import { cx } from '@twind/core';
import OstroDynamicContent from 'app/components/OstroDynamicContent';
import { BubbleMessage } from 'app/copied/types/ChatItems';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

type Props = {
  id: string;
  messages: BubbleMessage[];
  type?: 'tailor' | 'classic';
  className?: string;
  prependText?: React.ReactNode;
  textClassName?: string;
  subtextClassName?: string;
  onMessageChange?: (message: BubbleMessage) => void;
};

const MESSAGE_CHANGE_INTERVAL = 4000;

const variants = {
  initial: { opacity: 0, y: 34 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: [0.34, 1.56, 0.64, 1],
    },
  },
  exit: {
    y: -34,
    opacity: 0,
    transition: { duration: 0.1, ease: 'easeIn' },
  },
};

export default function BubbleMessages({
  id,
  type = 'tailor',
  messages,
  className,
  prependText,
  textClassName,
  subtextClassName,
  onMessageChange,
}: Props) {
  const [index, setIndex] = useState<number>(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % messages.length;
        if (newIndex === 0) {
          clearInterval(intervalId);
        }
        onMessageChange?.(messages[newIndex]);
        return newIndex;
      });
    }, MESSAGE_CHANGE_INTERVAL);

    return () => clearInterval(intervalId);
  }, [messages, onMessageChange]);

  return (
    <div className="flex flex-col w-full">
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          variants={messages.length > 1 ? variants : undefined}
          initial="initial"
          animate="animate"
          exit="exit"
          className={type === 'tailor' ? 'min-w-[230px]' : ''}
        >
          <div
            id={id}
            className={className}
          >
            <div className="flex items-center whitespace-nowrap">
              <OstroDynamicContent className={cx('whitespace-normal grow', textClassName)}>
                {prependText && <span>{prependText}</span>}
                <span
                  id="ostro-suggested-article-title"
                  dangerouslySetInnerHTML={{ __html: messages[index]?.text || '' }}
                />
              </OstroDynamicContent>
            </div>
            {messages[index]?.subtext && (
              <OstroDynamicContent
                id="ostro-suggested-article-description"
                className={subtextClassName}
                content={messages[index]?.subtext}
              />
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
